import { useEffect, useRef, useState } from "react";
import { Heart, MessageCircle, Send, Share2 } from "lucide-react";
import {
  fetchArticleEngagement,
  postArticleComment,
  postArticleLike,
  postArticleShare,
} from "../utils/articleEngagementApi";
import { getOrCreateVisitorId } from "../utils/visitorTracking";

const formatCommentDate = (value) =>
  new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const ArticleEngagement = ({ slug, title }) => {
  const [engagement, setEngagement] = useState({
    likes: 0,
    shares: 0,
    comments: [],
  });
  const [form, setForm] = useState({ name: "", message: "" });
  const [loading, setLoading] = useState(true);
  const [isLiking, setIsLiking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);
  const [visitorId, setVisitorId] = useState("");
  const [loadError, setLoadError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const commentFormRef = useRef(null);
  const commentTextareaRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    setVisitorId(getOrCreateVisitorId());
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadEngagement = async () => {
      setLoading(true);
      setLoadError("");

      try {
        const data = await fetchArticleEngagement(slug, visitorId);

        if (!isActive) {
          return;
        }

        setEngagement({
          likes: data?.likes || 0,
          shares: data?.shares || 0,
          comments: data?.comments || [],
        });
        setHasLiked(Boolean(data?.viewerHasLiked));
      } catch (error) {
        if (!isActive) {
          return;
        }

        setLoadError(
          error?.response?.data?.message ||
            "Likes and comments are unavailable right now."
        );
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadEngagement();

    return () => {
      isActive = false;
    };
  }, [slug, visitorId]);

  const handleLike = async () => {
    if (hasLiked || isLiking) {
      return;
    }

    setIsLiking(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      const data = await postArticleLike(slug, title, visitorId);
      setEngagement((prev) => ({
        ...prev,
        likes: data?.likes ?? prev.likes,
        shares: data?.shares ?? prev.shares,
        comments: data?.comments || prev.comments,
      }));
      setLoadError("");
      setHasLiked(Boolean(data?.viewerHasLiked ?? true));
      setStatusMessage(
        data?.alreadyLiked
          ? "Your like was already counted."
          : "Liked. Thanks for the signal."
      );
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Your like could not be saved."
      );
    } finally {
      setIsLiking(false);
    }
  };

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const sharePayload = {
      title,
      text: `Read this article: ${title}`,
      url,
    };

    setStatusMessage("");
    setErrorMessage("");
    setIsSharing(true);

    try {
      let shareMethod = "";

      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(sharePayload);
        shareMethod = "native-share";
      } else if (
        typeof navigator !== "undefined" &&
        navigator.clipboard?.writeText &&
        url
      ) {
        await navigator.clipboard.writeText(url);
        shareMethod = "clipboard-copy";
      } else {
        setStatusMessage("Copy the link from your browser address bar.");
        return;
      }

      const data = await postArticleShare(slug, {
        articleTitle: title,
        visitorId,
        shareMethod,
      });

      setEngagement((prev) => ({
        ...prev,
        likes: data?.likes ?? prev.likes,
        shares: data?.shares ?? prev.shares,
        comments: data?.comments || prev.comments,
      }));
      setStatusMessage(
        shareMethod === "clipboard-copy" ? "Link copied." : "Shared."
      );
    } catch (error) {
      if (error?.name !== "AbortError") {
        setErrorMessage("Share could not be completed.");
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleJumpToComments = () => {
    commentFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    commentTextareaRef.current?.focus();
  };

  const handleCommentSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const data = await postArticleComment(slug, {
        articleTitle: title,
        name: form.name,
        message: form.message,
      });

      setEngagement((prev) => ({
        ...prev,
        comments: data?.comment ? [data.comment, ...prev.comments] : prev.comments,
      }));
      setLoadError("");
      setForm((prev) => ({ ...prev, message: "" }));
      setStatusMessage("Comment posted.");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Your comment could not be saved."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="article-engagement" aria-labelledby="article-engagement-title">
      <div className="article-engagement__intro">
        <p className="article-engagement__eyebrow">Join the conversation</p>
        <div className="article-engagement__header">
          <div>
            <h2 id="article-engagement-title">Keep the discussion moving</h2>
            <p className="article-engagement__subcopy">
              Share it, leave a signal, or add a useful comment for the next reader.
            </p>
          </div>
          <div className="article-engagement__stats" aria-live="polite">
            <span>{loading ? "..." : engagement.likes} likes</span>
            <span>{loading ? "..." : engagement.shares} shares</span>
            <span>{loading ? "..." : engagement.comments.length} comments</span>
          </div>
        </div>
      </div>

      <div className="article-engagement__actions">
        <button
          type="button"
          className={`article-action ${hasLiked ? "article-action--active" : ""}`}
          onClick={handleLike}
          disabled={hasLiked || isLiking}
        >
          <Heart
            size={18}
            fill={hasLiked ? "currentColor" : "none"}
            aria-hidden="true"
          />
          <span>
            {hasLiked
              ? isLiking
                ? "Saving..."
                : "Liked"
              : isLiking
                ? "Saving..."
                : "Like"}
          </span>
        </button>

        <button
          type="button"
          className="article-action"
          onClick={handleShare}
          disabled={isSharing}
        >
          <Share2 size={18} aria-hidden="true" />
          <span>{isSharing ? "Saving..." : "Share"}</span>
        </button>

        <button
          type="button"
          className="article-action"
          onClick={handleJumpToComments}
        >
          <MessageCircle size={18} aria-hidden="true" />
          <span>Comment</span>
        </button>
      </div>

      {(loadError || statusMessage || errorMessage) && (
        <p
          className={`article-engagement__status ${
            loadError || errorMessage ? "article-engagement__status--error" : ""
          }`}
          role="status"
        >
          {loadError || errorMessage || statusMessage}
        </p>
      )}

      <div className="article-comments">
        <form
          className="article-comment-form"
          onSubmit={handleCommentSubmit}
          ref={commentFormRef}
        >
          <div className="article-comment-form__title-row">
            <div>
              <h3>Leave a comment</h3>
              <p>Questions, counterpoints, and sharp additions are welcome.</p>
            </div>
            <button
              type="submit"
              className="article-comment-form__submit"
              disabled={isSubmitting}
            >
              <Send size={16} aria-hidden="true" />
              <span>{isSubmitting ? "Posting..." : "Post comment"}</span>
            </button>
          </div>

          <div className="article-comment-form__grid">
            <label className="article-comment-form__field">
              <span>Name</span>
              <input
                type="text"
                value={form.name}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
                maxLength={60}
                required
              />
            </label>

            <label className="article-comment-form__field article-comment-form__field--full">
              <span>Comment</span>
              <textarea
                ref={commentTextareaRef}
                rows="5"
                value={form.message}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, message: event.target.value }))
                }
                maxLength={1200}
                required
              />
            </label>
          </div>
        </form>

        <div className="article-comments__list" aria-live="polite">
          {loading ? (
            <div className="article-comments__empty">Loading comments...</div>
          ) : loadError && engagement.comments.length === 0 ? (
            <div className="article-comments__empty">
              Comments will appear here when the service is available again.
            </div>
          ) : engagement.comments.length === 0 ? (
            <div className="article-comments__empty">
              No comments yet. Start the thread.
            </div>
          ) : (
            engagement.comments.map((comment) => (
              <article key={comment.id} className="article-comment">
                <div className="article-comment__meta">
                  <strong>{comment.name}</strong>
                  <span>{formatCommentDate(comment.createdAt)}</span>
                </div>
                <p>{comment.message}</p>
              </article>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default ArticleEngagement;
